import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { Button } from '../ui/button';
import { notificationsApi } from '../../services/api';
import { reportConnectionStatus } from '../../lib/connectionStatus';

/**
 * NotificationBell - Bell icon with unread count badge for header
 * Clicking navigates to the full notifications page
 */
export default function NotificationBell({ testId = 'notification-bell' }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  // Fetch unread count on mount and poll every 30 seconds
  useEffect(() => {
    let isMounted = true;
    
    const doFetch = async () => {
      try {
        const response = await notificationsApi.getUnreadCount();
        if (isMounted) {
          setUnreadCount(response.data?.count || 0);
        }
        reportConnectionStatus('notifications', response.data?.degraded);
      } catch (error) {
        console.error('Failed to fetch notification count:', error);
        reportConnectionStatus('notifications', true);
      }
    };
    
    doFetch();
    const interval = setInterval(doFetch, 30000);
    
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  // Listen for custom events to refresh count (e.g., after marking as read)
  useEffect(() => {
    const handleRefresh = async () => {
      try {
        const response = await notificationsApi.getUnreadCount();
        setUnreadCount(response.data?.count || 0);
      } catch (error) {
        console.error('Failed to refresh notification count:', error);
      }
    };
    window.addEventListener('notifications-updated', handleRefresh);
    return () => window.removeEventListener('notifications-updated', handleRefresh);
  }, []);

  return (
    <Button
      variant="ghost"
      size="icon"
      className="relative h-9 w-9"
      onClick={() => navigate('/notifications')}
      data-testid={testId}
      title="Notifications"
    >
      <Bell className="h-5 w-5" />
      {unreadCount > 0 && (
        <span 
          className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-xs font-medium"
          data-testid={`${testId}-count`}
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </Button>
  );
}
